/**
 * Amazon-style add-to-cart control for the APC product card.
 *
 * States:
 *   empty cart  → full-width "Add to cart" button
 *   qty === 1   → stepper with a trash icon on the left
 *   qty >= 2    → stepper with a minus icon on the left
 *
 * The "+" is hidden once the cart holds every unit in stock.
 * Cart writes reuse the theme's flow (bundled section rendering + cartUpdate
 * pubsub), so the header count and the cart drawer stay in sync.
 */
class APCCardATC extends HTMLElement {
  connectedCallback() {
    this.addButton = this.querySelector('[data-apc-add]');
    this.stepper = this.querySelector('[data-apc-stepper]');
    this.decButton = this.querySelector('[data-apc-dec]');
    this.incButton = this.querySelector('[data-apc-inc]');
    this.qtyLabel = this.querySelector('[data-apc-qty]');
    this.iconRemove = this.querySelector('[data-apc-icon-remove]');
    this.iconMinus = this.querySelector('[data-apc-icon-minus]');

    this.variantId = Number(this.dataset.variantId);
    this.max = Number(this.dataset.max) || 0;
    this.quantity = Number(this.dataset.quantity) || 0;
    this.lineKey = this.dataset.lineKey || '';
    this.busy = false;

    this.addButton && this.addButton.addEventListener('click', () => this.add());
    this.incButton && this.incButton.addEventListener('click', () => this.setQuantity(this.quantity + 1));
    this.decButton && this.decButton.addEventListener('click', () => this.setQuantity(this.quantity - 1));

    if (window.FoxTheme && FoxTheme.pubsub) {
      this.unsubscribe = FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, (event) =>
        this.syncFromCart(event && event.cart)
      );
    }
  }

  disconnectedCallback() {
    if (this.unsubscribe) this.unsubscribe();
  }

  get sectionsToBundle() {
    const sections = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections } })
    );
    return sections;
  }

  setBusy(state) {
    this.busy = state;
    this.classList.toggle('is-busy', state);
    [this.addButton, this.incButton, this.decButton].forEach((btn) => {
      if (btn) btn.disabled = state;
    });
  }

  async add() {
    if (this.busy) return;
    this.setBusy(true);
    try {
      // Line-item properties the draft-order checkout reads to charge the
      // customer's column price. Absent for guests and untagged customers.
      const item = { id: this.variantId, quantity: 1 };
      if (this.dataset.customPrice) {
        item.properties = {
          _custom_price: this.dataset.customPrice,
          _customer_tag: this.dataset.customerTag,
          _original_price: this.dataset.originalPrice,
          _has_custom_pricing: 'true',
        };
      }
      const body = JSON.stringify({
        items: [item],
        sections: this.sectionsToBundle,
      });
      const response = await fetch(FoxTheme.routes.cart_add_url, { ...FoxTheme.utils.fetchConfig(), body });
      const parsed = await response.json();
      if (parsed.status) throw new Error(parsed.description || parsed.message);
      await this.refreshCart(parsed.sections);
    } catch (error) {
      console.error('[apc-card-atc] add failed', error);
    } finally {
      this.setBusy(false);
    }
  }

  async setQuantity(quantity) {
    if (this.busy) return;
    const next = Math.max(0, this.max > 0 ? Math.min(quantity, this.max) : quantity);
    if (next === this.quantity) return;
    this.setBusy(true);
    try {
      const body = JSON.stringify({
        id: this.lineKey || String(this.variantId),
        quantity: next,
        sections: this.sectionsToBundle,
      });
      const response = await fetch(FoxTheme.routes.cart_change_url, { ...FoxTheme.utils.fetchConfig(), body });
      const parsed = await response.json();
      this.render(parsed);
      FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: parsed });
    } catch (error) {
      console.error('[apc-card-atc] quantity change failed', error);
    } finally {
      this.setBusy(false);
    }
  }

  /* /cart/add.js returns the added line, not the cart — fetch the cart for the true totals. */
  async refreshCart(sections) {
    const cart = await (
      await fetch(FoxTheme.routes.cart_url, { ...FoxTheme.utils.fetchConfig('json', 'GET') })
    ).json();
    if (sections) cart.sections = sections;
    this.render(cart);
    FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart });
  }

  syncFromCart(cart) {
    if (!cart || !cart.items || this.busy) return;
    this.render(cart);
  }

  render(cart) {
    const line = (cart.items || []).find((item) => item.variant_id === this.variantId);
    this.quantity = line ? line.quantity : 0;
    this.lineKey = line ? line.key : '';
    this.dataset.quantity = String(this.quantity);
    this.dataset.lineKey = this.lineKey;

    const inCart = this.quantity > 0;
    this.classList.toggle('is-in-cart', inCart);
    if (this.stepper) this.stepper.setAttribute('aria-hidden', inCart ? 'false' : 'true');
    if (this.qtyLabel) this.qtyLabel.textContent = String(Math.max(this.quantity, 1));

    // One unit left in the cart → the left button removes the line entirely.
    if (this.iconRemove) this.iconRemove.hidden = this.quantity > 1;
    if (this.iconMinus) this.iconMinus.hidden = this.quantity <= 1;

    if (this.incButton) this.incButton.hidden = this.max > 0 && this.quantity >= this.max;
  }
}

if (!customElements.get('apc-card-atc')) {
  customElements.define('apc-card-atc', APCCardATC);
}
